// src/features/professional/components/ProfessionalPortfolio.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Snackbar,
  CircularProgress,
  Skeleton,
  Divider,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  School as QualificationIcon,
  EmojiEvents as MilestoneIcon,
  Assignment as ProjectIcon,
  AttachFile as AttachIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { THEME_COLORS } from '../../../styles/theme';
import {
  getUserPortfolio,
  createPortfolioItem,
  updatePortfolioItem,
  deletePortfolioItem
} from '../services/portfolioService';
import { uploadFile } from '../../../services/storageService';
import ItemForm from './ItemForm';

const ProfessionalPortfolio = ({ userId }) => {
  const { currentUser } = useAuth();
  const [portfolio, setPortfolio] = useState({
    qualifications: [],
    milestones: [],
    projects: []
  });
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadPortfolioData();
  }, [userId]);

  const loadPortfolioData = async () => {
    try {
      setLoading(true);
      const data = await getUserPortfolio(userId);
      if (data) {
        setPortfolio(data);
      }
    } catch (error) {
      console.error('Error loading portfolio:', error);
      showNotification('Error loading portfolio data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleAddItem = (type) => {
    setDialogType(type);
    setEditingItem(null);
    setOpenDialog(true);
  };

  const handleEditItem = (type, item) => {
    setDialogType(type);
    setEditingItem(item);
    setOpenDialog(true);
  };

  const handleDeleteItem = async (type, itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deletePortfolioItem(userId, type, itemId);
        await loadPortfolioData();
        showNotification('Item deleted successfully');
      } catch (error) {
        console.error('Error deleting item:', error);
        showNotification('Error deleting item', 'error');
      }
    }
  };

  const handleSubmit = async (formData, files) => {
    try {
      setSubmitting(true);
      
      // Upload files if any
      const attachments = [];
      if (files && files.length > 0) {
        for (const file of files) {
          const uploadResult = await uploadFile(
            file, 
            `professional-portfolio/${userId}/${dialogType}/${Date.now()}_${file.name}`
          );
          attachments.push({
            name: file.name,
            url: uploadResult.url,
            uploadedAt: new Date()
          });
        }
      }

      const itemData = {
        ...formData,
        attachments: [...(formData.attachments || []), ...attachments],
        updatedAt: new Date()
      };

      if (editingItem) {
        await updatePortfolioItem(userId, dialogType, editingItem.id, itemData);
        showNotification('Item updated successfully');
      } else {
        await createPortfolioItem(userId, dialogType, itemData);
        showNotification('Item added successfully');
      }

      await loadPortfolioData();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving item:', error);
      showNotification('Error saving item', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQualifications = () => (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
            Qualifications & Certifications
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleAddItem('qualification')}
            sx={{ backgroundColor: THEME_COLORS.primary }}
          >
            Add Qualification
          </Button>
        </Box>
        
        {loading ? (
          <Stack spacing={1}>
            {[1, 2, 3].map((item) => (
              <Skeleton key={item} variant="rectangular" height={80} />
            ))}
          </Stack>
        ) : portfolio.qualifications.length === 0 ? (
          <Box textAlign="center" py={4}>
            <QualificationIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography color="textSecondary">
              No qualifications added yet. Start building your professional portfolio!
            </Typography>
          </Box>
        ) : (
          portfolio.qualifications.map((qual, index) => (
            <Accordion key={qual.id || index} sx={{ mb: 1 }}>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  '&:hover': { 
                    backgroundColor: 'rgba(39, 35, 97, 0.04)' 
                  }
                }}
              >
                <Box display="flex" alignItems="center" width="100%">
                  <QualificationIcon sx={{ mr: 2, color: THEME_COLORS.primary }} />
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {qual.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {qual.institution} • {qual.year}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip 
                      label={qual.type} 
                      size="small" 
                      sx={{ backgroundColor: 'rgba(39, 35, 97, 0.1)' }}
                    />
                    {qual.verified && (
                      <Chip 
                        label="Verified" 
                        size="small" 
                        color="success"
                      />
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="body2" paragraph>
                      {qual.description}
                    </Typography>
                    {qual.grade && (
                      <Typography variant="body2" gutterBottom>
                        <strong>Grade/Result:</strong> {qual.grade}
                      </Typography>
                    )}
                    {qual.skills && qual.skills.length > 0 && (
                      <Box mt={2}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Skills Acquired:</strong>
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {qual.skills.map((skill, idx) => (
                            <Chip key={idx} label={skill} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {qual.attachments && qual.attachments.length > 0 && (
                      <Box>
                        <Typography variant="body2" gutterBottom>
                          <strong>Documents:</strong>
                        </Typography>
                        <List dense>
                          {qual.attachments.map((file, idx) => (
                            <ListItem key={idx} sx={{ pl: 0 }}>
                              <ListItemIcon>
                                <AttachIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={file.name}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton 
                                  size="small" 
                                  onClick={() => window.open(file.url, '_blank')}
                                  title="View document"
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                    <Box mt={2} display="flex" gap={1}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditItem('qualification', qual)}
                        title="Edit qualification"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteItem('qualifications', qual.id)}
                        title="Delete qualification"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </CardContent>
    </Card>
  );

  const renderMilestones = () => (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
            Career Milestones
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleAddItem('milestone')}
            sx={{ backgroundColor: THEME_COLORS.primary }}
          >
            Add Milestone
          </Button>
        </Box>
        
        {loading ? (
          <Stack spacing={2}>
            {[1, 2].map((item) => (
              <Box key={item} display="flex" alignItems="start">
                <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
                <Box flex={1}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} />
                  <Skeleton variant="text" width="100%" height={60} />
                </Box>
              </Box>
            ))}
          </Stack>
        ) : portfolio.milestones.length === 0 ? (
          <Box textAlign="center" py={4}>
            <MilestoneIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography color="textSecondary">
              No milestones recorded yet. Document your career achievements!
            </Typography>
          </Box>
        ) : (
          portfolio.milestones.map((milestone, index) => (
            <Box key={milestone.id || index} display="flex" alignItems="start" mb={3}>
              <Avatar sx={{ bgcolor: THEME_COLORS.primary, mr: 2, mt: 0.5 }}>
                <MilestoneIcon />
              </Avatar>
              <Box flex={1}>
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {milestone.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {milestone.date} • {milestone.organization}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditItem('milestone', milestone)}
                      title="Edit milestone"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteItem('milestones', milestone.id)}
                      title="Delete milestone"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="body2" paragraph>
                  {milestone.description}
                </Typography>
                {milestone.impact && (
                  <Typography variant="body2">
                    <strong>Impact:</strong> {milestone.impact}
                  </Typography>
                )}
                {milestone.category && (
                  <Chip 
                    label={milestone.category} 
                    size="small" 
                    sx={{ mt: 1, backgroundColor: 'rgba(39, 35, 97, 0.1)' }}
                  />
                )}
              </Box>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );

  const renderProjects = () => (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
            Projects & Programme Participation
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleAddItem('project')}
            sx={{ backgroundColor: THEME_COLORS.primary }}
          >
            Add Project
          </Button>
        </Box>
        
        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} md={6} key={item}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
            ))}
          </Grid>
        ) : portfolio.projects.length === 0 ? (
          <Box textAlign="center" py={4}>
            <ProjectIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography color="textSecondary">
              No projects added yet. Showcase your professional work!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {portfolio.projects.map((project, index) => (
              <Grid item xs={12} md={6} key={project.id || index}>
                <Card variant="outlined" sx={{ height: '100%', position: 'relative' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ProjectIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                      <Typography variant="subtitle1" fontWeight="bold" flex={1}>
                        {project.title}
                      </Typography>
                      <Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditItem('project', project)}
                          title="Edit project"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteItem('projects', project.id)}
                          title="Delete project"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {project.type} • {project.duration}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {project.description}
                    </Typography>
                    {project.outcomes && (
                      <Typography variant="body2" paragraph>
                        <strong>Outcomes:</strong> {project.outcomes}
                      </Typography>
                    )}
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Chip 
                        label={project.status} 
                        size="small"
                        color={project.status === 'Completed' ? 'success' : 'primary'}
                      />
                      {project.role && (
                        <Typography variant="body2" fontWeight="bold">
                          Role: {project.role}
                        </Typography>
                      )}
                    </Box>
                    {project.attachments && project.attachments.length > 0 && (
                      <Box mt={2}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Attachments:</strong>
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {project.attachments.map((file, idx) => (
                            <Chip
                              key={idx}
                              label={file.name}
                              size="small"
                              onClick={() => window.open(file.url, '_blank')}
                              onDelete={() => {}} // Empty function to show delete icon as view icon
                              deleteIcon={<ViewIcon />}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {renderQualifications()}
      {renderMilestones()}
      {renderProjects()}
      
      {/* Add/Edit Item Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingItem ? 'Edit' : 'Add'} {
                dialogType === 'qualification' ? 'Qualification' : 
                dialogType === 'milestone' ? 'Milestone' : 'Project'
              }
            </Typography>
            <IconButton onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <ItemForm
            type={dialogType}
            initialData={editingItem}
            onSubmit={handleSubmit}
            onCancel={() => setOpenDialog(false)}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfessionalPortfolio;